import { UAContext } from "@quentin-sommer/react-useragent";
import { LoaderCircle, TriangleAlert } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { SheetOrModal } from "~/components/Modal";
import Button from "~/design/ui/Button";
import toastWithStyle from "~/helpers/toastWithStyle";
import { deleteResource } from "~/services/api";
import to from "~/utils/to";

function DeleteUserConfirmation({ visible, onClose, cookie }: { visible: boolean; onClose: () => void; operation: any }) {
  const [submitting, setSubmitting] = React.useState(false);
  const { uaResults } = React.useContext(UAContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const onSubmit = async () => {
    setSubmitting(true);

    const [err, res] = await to(deleteResource({ resource: "users", resourceId: cookie.user._id, token: cookie.token }));
    setSubmitting(false);

    if (err) return toastWithStyle({ message: err.response?.data?.message, options: { type: "error" }, uaResults, i18n });
    navigate("/login");
  };

  const Content = () => {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col items-center text-center">
          <span className="grid size-16 place-items-center rounded-full bg-danger-soft text-danger">
            <TriangleAlert className="size-7" strokeWidth={1.75} />
          </span>
          <h2 className="mt-4 font-display text-xl font-bold leading-relaxed text-ink">{t("modals.deleteUserConfirmation.deleteAccount")}</h2>
          <p className="mt-1 max-w-[34ch] text-sm leading-7 text-ink-2">{t("modals.deleteUserConfirmation.areYouSure")}</p>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            variant="danger"
            size="lg"
            block
            onClick={async () => {
              setSubmitting(true);
              const [] = await to(onSubmit());
              onClose();
              setSubmitting(false);
            }}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting && <LoaderCircle className="size-5 animate-spin" strokeWidth={1.75} />}
            {submitting ? t("modals.deleteUserConfirmation.pleaseWait") : t("modals.deleteUserConfirmation.delete")}
          </Button>
          <Button variant="ghost" size="lg" block onClick={onClose}>
            {t("modals.deleteUserConfirmation.discard")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <SheetOrModal visible={visible} onClose={onClose} label={t("modals.deleteUserConfirmation.deleteAccount")}>
      <Content />
    </SheetOrModal>
  );
}

export default DeleteUserConfirmation;
